import { TimeDisplayMode } from "@/shared/components/visualization/WorkspaceCtx";
import { CanonicalDataType } from "@/shared/domain/topics";

import type { FilterCondition } from "./state";

interface ConditionMeta {
  label: string;
  valueless: boolean;
  numericOnly: boolean;
  /** When true, this condition is only available for string/text columns. */
  stringOnly: boolean;
}

export const conditionMeta: Record<FilterCondition, ConditionMeta> = {
  contains: {
    label: "Contains",
    valueless: false,
    numericOnly: false,
    stringOnly: false,
  },
  does_not_contain: {
    label: "Does not contain",
    valueless: false,
    numericOnly: false,
    stringOnly: false,
  },
  is_empty: {
    label: "Is empty",
    valueless: true,
    numericOnly: false,
    stringOnly: false,
  },
  is_not_empty: {
    label: "Is not empty",
    valueless: true,
    numericOnly: false,
    stringOnly: false,
  },
  starts_with: {
    label: "Starts with",
    valueless: false,
    numericOnly: false,
    stringOnly: false,
  },
  ends_with: {
    label: "Ends with",
    valueless: false,
    numericOnly: false,
    stringOnly: false,
  },
  is_exactly: {
    label: "Is exactly",
    valueless: false,
    numericOnly: false,
    stringOnly: true,
  },
  matches_regex: {
    label: "Matches regex",
    valueless: false,
    numericOnly: false,
    stringOnly: false,
  },
  greater_than: {
    label: "Greater than",
    valueless: false,
    numericOnly: true,
    stringOnly: false,
  },
  less_than: {
    label: "Less than",
    valueless: false,
    numericOnly: true,
    stringOnly: false,
  },
  greater_than_or_equal: {
    label: "At least",
    valueless: false,
    numericOnly: true,
    stringOnly: false,
  },
  less_than_or_equal: {
    label: "At most",
    valueless: false,
    numericOnly: true,
    stringOnly: false,
  },
  between: {
    label: "Between",
    valueless: false,
    numericOnly: true,
    stringOnly: false,
  },
};

export function isValuelessCondition(condition: FilterCondition): boolean {
  return conditionMeta[condition].valueless;
}

export function needsSecondValue(condition: FilterCondition): boolean {
  return condition === "between";
}

export function isDatetimeColumn(dataType?: CanonicalDataType): boolean {
  return dataType === CanonicalDataType.Timestamp;
}

/**
 * Returns true when the time display mode represents an absolute point in time
 * (Local or UTC), meaning a calendar/datetime picker is appropriate.
 * Returns false for relative modes (Elapsed, ElapsedTime, Log) where a plain
 * numeric input is more appropriate.
 */
export function isAbsoluteTimeMode(mode: TimeDisplayMode): boolean {
  switch (mode) {
    case TimeDisplayMode.Local:
    case TimeDisplayMode.UTC:
      return true;
    case TimeDisplayMode.Elapsed:
    case TimeDisplayMode.ElapsedTime:
    case TimeDisplayMode.Log:
      return false;
    default: {
      const _exhaustive: never = mode;
      return _exhaustive;
    }
  }
}

/**
 * Returns an appropriate placeholder string for the primary filter text input
 * based on the current condition type.
 */
export function getFilterPlaceholder(
  isRegex: boolean,
  isBetween: boolean,
): string {
  if (isRegex) {
    return "Filter by regex";
  }
  if (isBetween) {
    return "Min";
  }
  return "Filter by text...";
}

const numericDataTypes = new Set<CanonicalDataType>([
  CanonicalDataType.Number,
  CanonicalDataType.Byte,
  CanonicalDataType.LatDegFloat,
  CanonicalDataType.LonDegFloat,
  CanonicalDataType.LatDegInt,
  CanonicalDataType.LonDegInt,
  CanonicalDataType.Timestamp,
]);

/**
 * Returns the filter conditions available for a given data type.
 *
 * - Timestamp columns get only the numeric comparators (greater_than,
 *   less_than, at least, at most, between). Text matching against a rendered
 *   timestamp (e.g. `contains "2025"`) is technically possible but
 *   user-hostile, and `is_empty` / `is_not_empty` are dead weight since
 *   timestamp cells are never empty in practice.
 * - Other numeric types (Number, Byte, lat/lon) get text + numeric + universal
 *   conditions, matching the existing behavior.
 * - Non-numeric types (and `undefined`, i.e. "All columns") get text +
 *   universal only — numeric comparators are hidden because they are
 *   meaningless on text.
 */
export function getConditionsForDataType(
  dataType?: CanonicalDataType,
): FilterCondition[] {
  const allConditions = Object.keys(conditionMeta) as FilterCondition[];

  // Timestamp: numeric comparators only. Mirrors how text columns hide the
  // numeric operators — filtering a time value by "contains" is as
  // nonsensical as filtering a name by "greater than".
  if (dataType === CanonicalDataType.Timestamp) {
    return allConditions.filter((c) => conditionMeta[c].numericOnly);
  }

  const includeNumeric =
    dataType !== undefined && numericDataTypes.has(dataType);

  return allConditions.filter((condition) => {
    const meta = conditionMeta[condition];
    if (meta.numericOnly && !includeNumeric) {
      return false;
    }
    if (meta.stringOnly && includeNumeric) {
      return false;
    }
    return true;
  });
}

/**
 * Returns true when a datetime picker should be shown for this column's filter input.
 * Requires a Timestamp column, an absolute time display mode, a numeric (non-valueless)
 * condition, and a condition that takes a value.
 */
export function shouldUseDatetimePicker(
  dataType: CanonicalDataType | undefined,
  timeDisplayMode: TimeDisplayMode | undefined,
  condition: FilterCondition,
): boolean {
  return (
    isDatetimeColumn(dataType) &&
    timeDisplayMode !== undefined &&
    isAbsoluteTimeMode(timeDisplayMode) &&
    conditionMeta[condition].numericOnly
  );
}
